import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  adminAuditLogsApi,
  type AuditLogEntry,
  type AuditEventType,
  type AuditLogsEvidenceNotice,
} from '../api/client';

/** F-2 (2026-06-09 DCS): フィルタプルダウンに並べる event_type 一覧 */
const EVENT_TYPE_OPTIONS: AuditEventType[] = [
  'admin.user.deleted',
  'admin.user.exported',
  'consent.granted',
  'consent.revoked',
  'data_request.approved',
  'data_request.rejected',
  'prompt.deleted',
  'settings.viewed',
  'settings.updated',
  'tenant.provisioned',
  'voice_file.deleted',
  'transcript.nulled',
  'transcript.never_stored',
];

/** F-3 (DCS): 破棄証跡レポートで対象になる event_type 3 種（固定） */
const DISCARD_EVENT_TYPES: AuditEventType[] = [
  'voice_file.deleted',
  'transcript.nulled',
  'transcript.never_stored',
];

function formatDateForApi(value: string, endOfDay = false): string | undefined {
  if (!value) return undefined;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return undefined;
  const d = new Date(ms);
  if (endOfDay) {
    d.setHours(23, 59, 59, 999);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return d.toISOString();
}

/** 「先月 1 日〜先月末日」を YYYY-MM-DD で返す（F-3 デフォルト期間） */
function getLastMonthRange(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: fmt(start), to: fmt(end) };
}

/** event_metadata を読みやすい JSON で表示 */
function formatMeta(meta: Record<string, unknown> | null | undefined): string {
  if (!meta) return '';
  try {
    return JSON.stringify(meta, null, 2);
  } catch {
    return String(meta);
  }
}

/** ブラウザでファイルダウンロードを発火する小ヘルパ */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** ADM-S08 監査ログ閲覧 + 破棄証跡レポート出力（F-2 + F-3） */
export default function AdminAuditLogs() {
  const { t } = useTranslation();
  const { getToken, user } = useAuth();

  // フィルタ
  const [eventType, setEventType] = useState<string>('');
  const [filterFrom, setFilterFrom] = useState<string>('');
  const [filterTo, setFilterTo] = useState<string>('');

  // 一覧データ
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  // 破棄証跡の記録開始日の注記（課題 No.61）。BE が返すときのみ表示する
  const [evidenceNotice, setEvidenceNotice] = useState<AuditLogsEvidenceNotice | null>(null);

  // F-3 破棄証跡レポート
  const lastMonth = useMemo(() => getLastMonthRange(), []);
  const [reportFrom, setReportFrom] = useState<string>(lastMonth.from);
  const [reportTo, setReportTo] = useState<string>(lastMonth.to);
  const [reportFormat, setReportFormat] = useState<'csv' | 'json'>('csv');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setListError(null);
    setLoading(true);
    adminAuditLogsApi
      .list(
        {
          event_type: eventType || undefined,
          from: formatDateForApi(filterFrom),
          to: formatDateForApi(filterTo, true),
          limit: 100,
        },
        token
      )
      .then((res) => {
        // BE 契約は `data`（openapi: AuditLogListResponse）。
        // 旧実装は `items` を読んでいたため一覧が常に空になっていた（No.4 と同型の解釈バグ）。
        setItems(res.data ?? res.items ?? []);
        setEvidenceNotice(res.evidenceNotice ?? null);
      })
      .catch((err) => {
        setListError(err instanceof Error ? err.message : t('admin.auditLogs.errorLoad'));
        setItems([]);
        setEvidenceNotice(null);
      })
      .finally(() => setLoading(false));
  }, [eventType, filterFrom, filterTo, getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    const token = getToken();
    if (!token) return;
    setExportError(null);
    setExporting(true);
    try {
      const blob = await adminAuditLogsApi.export(
        {
          event_type: DISCARD_EVENT_TYPES.join(','),
          from: formatDateForApi(reportFrom),
          to: formatDateForApi(reportTo, true),
          format: reportFormat,
        },
        token
      );
      const ext = reportFormat === 'json' ? 'json' : 'csv';
      const tenant = user?.orgId ? `_${user.orgId.slice(0, 8)}` : '';
      const periodLabel = reportFrom && reportTo
        ? `_${reportFrom}_${reportTo}`
        : '';
      const filename = `discard_audit${tenant}${periodLabel}.${ext}`;
      triggerDownload(blob, filename);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : t('admin.auditLogs.errorExport'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page admin-audit-logs-page">
      <h1>{t('admin.auditLogs.title')}</h1>
      <p className="admin-settings-intro">{t('admin.auditLogs.intro')}</p>

      {/* F-3: 破棄証跡レポート出力パネル（データ最小化の運用証跡） */}
      <section className="admin-card admin-audit-export-card">
        <h2>{t('admin.auditLogs.exportTitle')}</h2>
        <p className="admin-form-hint">{t('admin.auditLogs.exportIntro')}</p>

        {/*
          破棄証跡の記録開始日の注記（課題 No.61）。
          記録開始（2026-06-13）より前の破棄には証跡が存在しないため、
          レポート単体を見た人が「破棄していない」と誤読しないよう常設表示する。
          日付・文面は BE（evidenceNotice）が返す値をそのまま使う（FE でハードコードしない）。
        */}
        {evidenceNotice && (
          <p className="admin-audit-evidence-notice" role="note">
            <span aria-hidden="true">ℹ️</span>{' '}
            {evidenceNotice.note}
          </p>
        )}

        <div className="admin-audit-export-grid">
          <div className="admin-form-group">
            <label htmlFor="export-from">{t('admin.auditLogs.from')}</label>
            <input
              id="export-from"
              type="date"
              value={reportFrom}
              onChange={(e) => setReportFrom(e.target.value)}
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="export-to">{t('admin.auditLogs.to')}</label>
            <input
              id="export-to"
              type="date"
              value={reportTo}
              onChange={(e) => setReportTo(e.target.value)}
            />
          </div>
        </div>

        <div className="admin-form-group">
          <p className="admin-form-label-text">{t('admin.auditLogs.exportTargets')}</p>
          <ul className="admin-audit-export-targets">
            {DISCARD_EVENT_TYPES.map((evt) => (
              <li key={evt}>
                <span className="admin-audit-check" aria-hidden="true">✓</span>{' '}
                {t(`admin.auditLogs.eventType.${evt}`, { defaultValue: evt })}
                <code className="admin-audit-event-code">{evt}</code>
              </li>
            ))}
          </ul>
        </div>

        <div className="admin-form-group">
          <label htmlFor="export-format">{t('admin.auditLogs.format')}</label>
          <select
            id="export-format"
            value={reportFormat}
            onChange={(e) => setReportFormat(e.target.value as 'csv' | 'json')}
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
          </select>
        </div>

        {exportError && <p className="admin-form-error">{exportError}</p>}

        <button
          type="button"
          className="admin-btn-primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? t('common.loading') : t('admin.auditLogs.exportButton')}
        </button>
        <p className="admin-form-hint admin-audit-export-note">
          {t('admin.auditLogs.exportNote')}
        </p>
      </section>

      {/* F-2: 監査ログ一覧 + フィルタ */}
      <section className="admin-card">
        <h2>{t('admin.auditLogs.listTitle')}</h2>

        <div className="admin-audit-filter-grid">
          <div className="admin-form-group">
            <label htmlFor="filter-event-type">{t('admin.auditLogs.eventTypeFilter')}</label>
            <select
              id="filter-event-type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            >
              <option value="">{t('admin.auditLogs.allEvents')}</option>
              {EVENT_TYPE_OPTIONS.map((evt) => (
                <option key={evt} value={evt}>
                  {t(`admin.auditLogs.eventType.${evt}`, { defaultValue: evt })}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-form-group">
            <label htmlFor="filter-from">{t('admin.auditLogs.from')}</label>
            <input
              id="filter-from"
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="filter-to">{t('admin.auditLogs.to')}</label>
            <input
              id="filter-to"
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
            />
          </div>
          <div className="admin-form-group admin-audit-reload">
            <button type="button" className="admin-btn-secondary" onClick={load} disabled={loading}>
              {loading ? t('common.loading') : t('admin.auditLogs.reload')}
            </button>
          </div>
        </div>

        {listError && (
          <div className="admin-error" role="alert">
            <p>{listError}</p>
            <button type="button" className="dashboard-error-retry" onClick={load}>
              {t('dashboard.retry')}
            </button>
          </div>
        )}

        {!loading && !listError && items.length === 0 && (
          <p className="admin-form-hint">{t('admin.auditLogs.empty')}</p>
        )}

        {items.length > 0 && (
          <div className="admin-audit-table-wrapper">
            <table className="admin-audit-table">
              <thead>
                <tr>
                  <th>{t('admin.auditLogs.col.createdAt')}</th>
                  <th>{t('admin.auditLogs.col.eventType')}</th>
                  <th>{t('admin.auditLogs.col.actor')}</th>
                  <th>{t('admin.auditLogs.col.resource')}</th>
                  <th>{t('admin.auditLogs.col.metadata')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="admin-audit-cell-time">{entry.created_at}</td>
                    <td>
                      <span className="admin-audit-event-badge">
                        {t(`admin.auditLogs.eventType.${entry.event_type}`, { defaultValue: entry.event_type })}
                      </span>
                      <br />
                      <code className="admin-audit-event-code">{entry.event_type}</code>
                    </td>
                    <td>
                      {entry.actor_user_id ?? '—'}
                      <br />
                      <span className="admin-audit-actor-role">{entry.actor_role ?? ''}</span>
                    </td>
                    <td>
                      {entry.resource_type ?? '—'}
                      {entry.resource_id ? <><br /><code>{entry.resource_id}</code></> : null}
                    </td>
                    <td>
                      {entry.event_metadata
                        ? <pre className="admin-audit-meta-pre">{formatMeta(entry.event_metadata)}</pre>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.dashboard')}</Link>
      </p>
    </div>
  );
}
